import type { CSSProperties } from "react";

export const CHART_SCALE_VARS = [
  "var(--chart-scale-01)",
  "var(--chart-scale-02)",
  "var(--chart-scale-03)",
  "var(--chart-scale-04)",
  "var(--chart-scale-05)",
  "var(--chart-scale-06)",
  "var(--chart-scale-07)",
] as const;

/**
 * Readable text color for each scale step. Paired tokens rather than a computed
 * luminance: the step colors are CSS variables, so their value is only known to
 * the cascade — resolving them in JS needs `getComputedStyle`, which is absent
 * during SSR and blind to the grayscale override that lives on a module root.
 */
export const CHART_SCALE_FG_VARS = [
  "var(--chart-scale-01-fg)",
  "var(--chart-scale-02-fg)",
  "var(--chart-scale-03-fg)",
  "var(--chart-scale-04-fg)",
  "var(--chart-scale-05-fg)",
  "var(--chart-scale-06-fg)",
  "var(--chart-scale-07-fg)",
] as const;

export const CHART_SCALE_STEP_COUNT = CHART_SCALE_VARS.length;

export type ChartScaleVars = typeof CHART_SCALE_VARS;

export const chartScaleCssVars = {
  scale01: CHART_SCALE_VARS[0],
  scale02: CHART_SCALE_VARS[1],
  scale03: CHART_SCALE_VARS[2],
  scale04: CHART_SCALE_VARS[3],
  scale05: CHART_SCALE_VARS[4],
  scale06: CHART_SCALE_VARS[5],
  scale07: CHART_SCALE_VARS[6],
  patternColor: "var(--chart-scale-pattern-color)",
} as const;

export function chartScaleStepIndex(index: number): number {
  if (!Number.isFinite(index)) {
    return 0;
  }
  return Math.min(Math.max(Math.round(index), 0), CHART_SCALE_STEP_COUNT - 1);
}

/** Step for a 0–1 ratio, where 0 is the lightest step and 1 the darkest. */
export function chartScaleStepForRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) {
    return 0;
  }
  return chartScaleStepIndex(ratio * (CHART_SCALE_STEP_COUNT - 1));
}

export function chartScaleColor(index: number): string {
  return CHART_SCALE_VARS[chartScaleStepIndex(index)];
}

export function chartScaleForeground(index: number): string {
  return CHART_SCALE_FG_VARS[chartScaleStepIndex(index)];
}

/** Gradient color stops across every step, spread over the given percent span. */
export function chartScaleGradientStops(
  startPercent = 0,
  endPercent = 100
): string[] {
  const lastIndex = CHART_SCALE_STEP_COUNT - 1;
  const span = endPercent - startPercent;
  return CHART_SCALE_VARS.map((color, index) => {
    const offset = startPercent + (index / lastIndex) * span;
    return `${color} ${Math.round(offset * 100) / 100}%`;
  });
}

/** CSS `linear-gradient` across every step, for legend ramps. */
export function chartScaleGradient(direction = "to right"): string {
  return `linear-gradient(${direction}, ${chartScaleGradientStops().join(", ")})`;
}

/**
 * `var()` references, not literals: this is an inline style, so it wins the
 * token *name* against `.dark`, but its value is still substituted from the
 * element's own cascade. Literals here pinned a grayscale chart to the light
 * neutrals in both themes; the values live in `app/handyout-tokens.css`.
 */
const CHART_SCALE_GRAYSCALE_OVERRIDE = {
  "--chart-scale-01": "var(--handyout-scale-mono-01)",
  "--chart-scale-02": "var(--handyout-scale-mono-02)",
  "--chart-scale-03": "var(--handyout-scale-mono-03)",
  "--chart-scale-04": "var(--handyout-scale-mono-04)",
  "--chart-scale-05": "var(--handyout-scale-mono-05)",
  "--chart-scale-06": "var(--handyout-scale-mono-06)",
  "--chart-scale-07": "var(--handyout-scale-mono-07)",
  "--chart-scale-01-fg": "var(--handyout-scale-mono-01-fg)",
  "--chart-scale-02-fg": "var(--handyout-scale-mono-02-fg)",
  "--chart-scale-03-fg": "var(--handyout-scale-mono-03-fg)",
  "--chart-scale-04-fg": "var(--handyout-scale-mono-04-fg)",
  "--chart-scale-05-fg": "var(--handyout-scale-mono-05-fg)",
  "--chart-scale-06-fg": "var(--handyout-scale-mono-06-fg)",
  "--chart-scale-07-fg": "var(--handyout-scale-mono-07-fg)",
  "--chart-scale-pattern-color": "var(--handyout-scale-mono-pattern)",
} as CSSProperties;

/** Scopes the scale to neutrals for a module rendered with `grayscale`. */
export function chartScaleGrayscaleStyle(
  grayscale?: boolean
): CSSProperties | undefined {
  return grayscale ? CHART_SCALE_GRAYSCALE_OVERRIDE : undefined;
}
